/**
 * Mộc By Dylan — Collectible Universe Animation System
 * Soft, cinematic, emotionally warm interactions
 */

/* ── MocReveal — Scroll-triggered fade + drift ───────────── */
class MocReveal extends HTMLElement {
  connectedCallback() {
    // ── Performance debug flag ────────────────────────────────────────────
    // Content is hidden by CSS until 'is-visible' is present (same contract
    // as the prefers-reduced-motion path below). Add the class immediately so
    // the element is readable even with animations disabled.
    if (window.__PERFORMANCE_FLAGS__?.disableAnimations) {
      this.classList.add('is-visible');
      return;
    }
    // ─────────────────────────────────────────────────────────────────────
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.classList.add('is-visible');
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          this.classList.add('is-visible');
          observer.disconnect();
        }
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );

    observer.observe(this);
  }
}

/* ── MocParallax — Slow scroll depth illusion ────────────── */
class MocParallax extends HTMLElement {
  #raf = null;
  #controller = new AbortController();

  connectedCallback() {
    if (window.__PERFORMANCE_FLAGS__?.disableAnimations) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const speed = parseFloat(this.dataset.speed ?? '0.25');

    const onScroll = () => {
      if (this.#raf) return;
      this.#raf = requestAnimationFrame(() => {
        this.#raf = null;
        const rect = this.getBoundingClientRect();
        const viewH = window.innerHeight;
        const center = rect.top + rect.height / 2 - viewH / 2;
        const offset = center * speed;
        this.style.setProperty('--moc-parallax-offset', `${offset}px`);
      });
    };

    window.addEventListener('scroll', onScroll, {
      passive: true,
      signal: this.#controller.signal,
    });

    onScroll();
  }

  disconnectedCallback() {
    this.#controller.abort();
    if (this.#raf) cancelAnimationFrame(this.#raf);
  }
}

/* ── MocHoverLift — Card hover elevation ─────────────────── */
class MocHoverLift extends HTMLElement {
  #controller = new AbortController();

  connectedCallback() {
    if (window.__PERFORMANCE_FLAGS__?.disableAnimations) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const { signal } = this.#controller;

    this.addEventListener('mouseenter', () => {
      this.style.transform = 'translateY(-6px)';
      this.style.boxShadow = '0 24px 64px rgba(139, 98, 69, 0.22)';
    }, { signal });

    this.addEventListener('mouseleave', () => {
      this.style.transform = '';
      this.style.boxShadow = '';
    }, { signal });
  }

  disconnectedCallback() {
    this.#controller.abort();
  }
}

/* ── MocScrollProgress — Thin warm progress bar at top ────── */
class MocScrollProgress extends HTMLElement {
  #raf = null;
  #controller = new AbortController();

  connectedCallback() {
    if (window.__PERFORMANCE_FLAGS__?.disableAnimations) return;
    this.setAttribute('aria-hidden', 'true');
    this.style.cssText = `
      position: fixed;
      top: 0; left: 0;
      height: 2px;
      width: 0%;
      background: linear-gradient(to right, var(--moc-honey), var(--moc-wood));
      z-index: 9999;
      pointer-events: none;
      transition: width 80ms linear;
    `;

    const update = () => {
      if (this.#raf) return;
      this.#raf = requestAnimationFrame(() => {
        this.#raf = null;
        const scrolled = window.scrollY;
        const total = document.documentElement.scrollHeight - window.innerHeight;
        const pct = total > 0 ? (scrolled / total) * 100 : 0;
        this.style.width = `${pct}%`;
      });
    };

    window.addEventListener('scroll', update, {
      passive: true,
      signal: this.#controller.signal,
    });
  }

  disconnectedCallback() {
    this.#controller.abort();
    if (this.#raf) cancelAnimationFrame(this.#raf);
  }
}

/* ── MocWorldCard — Collection world card hover color bloom ── */
class MocWorldCard extends HTMLElement {
  #controller = new AbortController();

  connectedCallback() {
    if (window.__PERFORMANCE_FLAGS__?.disableAnimations) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const accentColor = this.dataset.worldColor ?? 'var(--moc-linen)';
    const { signal } = this.#controller;

    this.addEventListener('mouseenter', () => {
      this.style.setProperty('--world-card-bg', accentColor);
    }, { signal });

    this.addEventListener('mouseleave', () => {
      this.style.removeProperty('--world-card-bg');
    }, { signal });
  }

  disconnectedCallback() {
    this.#controller.abort();
  }
}

/* ── MocStaggerGroup — Staggered reveal for child elements ─── */
class MocStaggerGroup extends HTMLElement {
  connectedCallback() {
    if (window.__PERFORMANCE_FLAGS__?.disableAnimations) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const children = [...this.children];
    children.forEach((child, i) => {
      child.style.opacity = '0';
      child.style.transform = 'translateY(20px)';
      child.style.transition = `opacity 700ms var(--moc-ease), transform 700ms var(--moc-ease)`;
      child.style.transitionDelay = `${i * 100}ms`;
    });

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          children.forEach(child => {
            child.style.opacity = '1';
            child.style.transform = 'translateY(0)';
          });
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(this);
  }
}

/* ── Register all custom elements ────────────────────────── */
if (!customElements.get('moc-reveal'))
  customElements.define('moc-reveal', MocReveal);

if (!customElements.get('moc-parallax'))
  customElements.define('moc-parallax', MocParallax);

if (!customElements.get('moc-hover-lift'))
  customElements.define('moc-hover-lift', MocHoverLift);

if (!customElements.get('moc-scroll-progress'))
  customElements.define('moc-scroll-progress', MocScrollProgress);

if (!customElements.get('moc-world-card'))
  customElements.define('moc-world-card', MocWorldCard);

if (!customElements.get('moc-stagger-group'))
  customElements.define('moc-stagger-group', MocStaggerGroup);
